import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PortalProfile from '../components/PortalProfile';
import useSessionManager from '../hooks/useSessionManager';

const API = process.env.REACT_APP_BACKEND_URL;

function PortalDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Quote editing states
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [editedQuote, setEditedQuote] = useState(null);
  
  // Booking editing states
  const [isEditingBooking, setIsEditingBooking] = useState(false);
  const [editedBooking, setEditedBooking] = useState(null);

  // Session management hook
  const handleSessionExpired = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_email');
    alert('Your session has expired due to inactivity. Please log in again.');
    navigate('/portal');
  };

  const { updateActivity } = useSessionManager(isAuthenticated, handleSessionExpired);

  // Filter states
  const [bookingFilters, setBookingFilters] = useState({
    searchText: '',
    startDate: '',
    endDate: ''
  });
  const [quoteFilters, setQuoteFilters] = useState({
    searchText: '',
    startDate: '',
    endDate: ''
  });
  
  // Dashboard filter state (for clickable summary cards)
  const [dashboardFilter, setDashboardFilter] = useState('all'); // 'all', 'active', 'completed', 'quotes'

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('portal_token');
    const savedEmail = localStorage.getItem('portal_email');
    
    if (!token || !savedEmail) {
      navigate('/portal');
      return;
    }

    setEmail(savedEmail);
    setIsAuthenticated(true);
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      const savedEmail = localStorage.getItem('portal_email');
      const response = await axios.get(`${API}/api/portal/dashboard-data?email=${encodeURIComponent(savedEmail)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setBookings(response.data.bookings);
      setQuotes(response.data.quotes);
      setSummary(response.data.stats);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_email');
        alert('Session expired. Please login again.');
        navigate('/portal');
      } else {
        alert('Failed to load dashboard data');
        setLoading(false);
      }
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_email');
    navigate('/portal');
  };

  // Quote management functions
  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setEditedQuote({...quote});
    setIsEditingQuote(false);
    setShowQuoteModal(true);
  };

  const handleEditQuote = () => {
    setIsEditingQuote(true);
  };

  const handleSaveQuote = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      await axios.put(
        `${API}/api/quotes/${selectedQuote.id}`,
        {
          pickup_location: editedQuote.pickup_location,
          delivery_location: editedQuote.delivery_location,
          container_type: editedQuote.container_type,
          cargo_weight: editedQuote.cargo_weight,
          pickup_date: editedQuote.pickup_date,
          delivery_date: editedQuote.delivery_date,
          special_instructions: editedQuote.special_instructions
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Refresh dashboard data
      fetchDashboardData(token);
      setIsEditingQuote(false);
      alert('Quote updated successfully!');
    } catch (error) {
      console.error('Error updating quote:', error);
      alert('Failed to update quote. Please try again.');
    }
  };

  const handleConvertToBooking = async () => {
    if (!window.confirm('Convert this quote to a booking?')) return;
    
    try {
      const token = localStorage.getItem('portal_token');
      const response = await axios.post(
        `${API}/api/quotes/${selectedQuote.id}/convert-to-booking`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      alert('Quote converted to booking successfully!');
      setShowQuoteModal(false);
      fetchDashboardData(token);
      setActiveTab('bookings');
    } catch (error) {
      console.error('Error converting quote:', error);
      alert(error.response?.data?.detail || 'Failed to convert quote to booking.');
    }
  };

  const handleCancelEdit = () => {
    setEditedQuote({...selectedQuote});
    setIsEditingQuote(false);
  };

  // Booking management functions
  const handleEditBooking = () => {
    // Parse chassis days from accessorials if not already stored
    let regularDays = selectedBooking.regular_chassis_days || 0;
    let triaxleDays = selectedBooking.triaxle_chassis_days || 0;
    
    // If not stored separately, try to extract from accessorials
    if (!regularDays && !triaxleDays && selectedBooking.accessorials) {
      selectedBooking.accessorials.forEach(acc => {
        const name = acc.name.toLowerCase();
        if (name.includes('regular chassis')) {
          const match = name.match(/(\d+)\s*day/);
          if (match) regularDays = parseInt(match[1]);
        } else if (name.includes('triaxle chassis')) {
          const match = name.match(/(\d+)\s*day/);
          if (match) triaxleDays = parseInt(match[1]);
        } else if (name.includes('chassis') && !name.includes('triaxle') && !name.includes('regular')) {
          // Generic "Chassis" - assume regular
          const match = name.match(/(\d+)\s*day/);
          if (match) regularDays = parseInt(match[1]);
        }
      });
    }
    
    setEditedBooking({
      ...selectedBooking,
      regular_chassis_days: regularDays,
      triaxle_chassis_days: triaxleDays
    });
    setIsEditingBooking(true);
  };

  const handleSaveBooking = async () => {
    try {
      // Validate chassis days (minimum 1 day if provided)
      if (editedBooking.regular_chassis_days && editedBooking.regular_chassis_days < 1) {
        alert('Regular chassis days must be at least 1 day');
        return;
      }
      if (editedBooking.triaxle_chassis_days && editedBooking.triaxle_chassis_days < 1) {
        alert('Triaxle chassis days must be at least 1 day');
        return;
      }

      const token = localStorage.getItem('portal_token');
      await axios.put(
        `${API}/api/bookings/${selectedBooking.id}`,
        {
          pickup_location: editedBooking.pickup_location,
          delivery_location: editedBooking.delivery_location,
          pickup_date: editedBooking.pickup_date,
          delivery_date: editedBooking.delivery_date,
          pickup_time: editedBooking.pickup_time,
          delivery_time: editedBooking.delivery_time,
          container_type: editedBooking.container_type,
          cargo_weight: editedBooking.cargo_weight,
          special_instructions: editedBooking.special_instructions,
          container_trailer_numbers: editedBooking.container_trailer_numbers,
          bol_numbers: editedBooking.bol_numbers,
          regular_chassis_days: editedBooking.regular_chassis_days,
          triaxle_chassis_days: editedBooking.triaxle_chassis_days
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Update local state
      setSelectedBooking(editedBooking);
      
      // Refresh dashboard data
      fetchDashboardData(token);
      setIsEditingBooking(false);
      alert('Booking updated successfully! Rate has been recalculated based on chassis days.');
    } catch (error) {
      console.error('Error updating booking:', error);
      alert(error.response?.data?.detail || 'Failed to update booking. Please try again.');
    }
  };

  const handleCancelBookingEdit = () => {
    setEditedBooking({...selectedBooking});
    setIsEditingBooking(false);
  };

  const handleAddContainerNumber = () => {
    const newContainers = [...(editedBooking.container_trailer_numbers || [])];
    newContainers.push({ number: '', type: 'container' });
    setEditedBooking({...editedBooking, container_trailer_numbers: newContainers});
  };

  const handleRemoveContainerNumber = (index) => {
    const newContainers = [...editedBooking.container_trailer_numbers];
    newContainers.splice(index, 1);
    setEditedBooking({...editedBooking, container_trailer_numbers: newContainers});
  };

  const handleUpdateContainerNumber = (index, value) => {
    const newContainers = [...editedBooking.container_trailer_numbers];
    newContainers[index] = { ...newContainers[index], number: value };
    setEditedBooking({...editedBooking, container_trailer_numbers: newContainers});
  };

  const handleAddBolNumber = () => {
    const newBols = [...(editedBooking.bol_numbers || [])];
    newBols.push('');
    setEditedBooking({...editedBooking, bol_numbers: newBols});
  };

  const handleRemoveBolNumber = (index) => {
    const newBols = [...editedBooking.bol_numbers];
    newBols.splice(index, 1);
    setEditedBooking({...editedBooking, bol_numbers: newBols});
  };

  const handleUpdateBolNumber = (index, value) => {
    const newBols = [...editedBooking.bol_numbers];
    newBols[index] = value;
    setEditedBooking({...editedBooking, bol_numbers: newBols});
  };

  // Filter functions
  const filterBookings = (bookingsData) => {
    return bookingsData.filter(booking => {
      // Dashboard filter (from clickable cards)
      if (dashboardFilter === 'active') {
        // Active: pending, confirmed, in_transit, picked_up
        const activeStatuses = ['pending', 'confirmed', 'in_transit', 'picked_up'];
        if (!activeStatuses.includes(booking.status)) return false;
      } else if (dashboardFilter === 'completed') {
        // Completed: delivered, completed
        const completedStatuses = ['delivered', 'completed'];
        if (!completedStatuses.includes(booking.status)) return false;
      }
      
      // Text search (booking reference, container, BOL)
      if (bookingFilters.searchText) {
        const searchLower = bookingFilters.searchText.toLowerCase();
        const matchText = 
          (booking.booking_reference && booking.booking_reference.toLowerCase().includes(searchLower)) ||
          (booking.container_trailer_numbers && booking.container_trailer_numbers.some(c => 
            c.number.toLowerCase().includes(searchLower)
          )) ||
          (booking.bill_of_lading_numbers && booking.bill_of_lading_numbers.some(b => 
            b.number.toLowerCase().includes(searchLower)
          )) ||
          (booking.pickup_location && booking.pickup_location.toLowerCase().includes(searchLower)) ||
          (booking.delivery_location && booking.delivery_location.toLowerCase().includes(searchLower));
        
        if (!matchText) return false;
      }

      // Date range filter
      if (bookingFilters.startDate || bookingFilters.endDate) {
        const bookingDate = new Date(booking.created_at);
        if (bookingFilters.startDate) {
          const start = new Date(bookingFilters.startDate);
          if (bookingDate < start) return false;
        }
        if (bookingFilters.endDate) {
          const end = new Date(bookingFilters.endDate);
          end.setHours(23, 59, 59);
          if (bookingDate > end) return false;
        }
      }

      return true;
    });
  };

  const filterQuotes = (quotesData) => {
    return quotesData.filter(quote => {
      // Text search
      if (quoteFilters.searchText) {
        const searchLower = quoteFilters.searchText.toLowerCase();
        const matchText = 
          (quote.pickup_location && quote.pickup_location.toLowerCase().includes(searchLower)) ||
          (quote.delivery_location && quote.delivery_location.toLowerCase().includes(searchLower)) ||
          (quote.container_type && quote.container_type.toLowerCase().includes(searchLower));
        
        if (!matchText) return false;
      }

      // Date range filter
      if (quoteFilters.startDate || quoteFilters.endDate) {
        const quoteDate = new Date(quote.created_at);
        if (quoteFilters.startDate) {
          const start = new Date(quoteFilters.startDate);
          if (quoteDate < start) return false;
        }
        if (quoteFilters.endDate) {
          const end = new Date(quoteFilters.endDate);
          end.setHours(23, 59, 59);
          if (quoteDate > end) return false;
        }
      }

      return true;
    });
  };

  const updateBookingFilter = (field, value) => {
    setBookingFilters(prev => ({ ...prev, [field]: value }));
  };

  const updateQuoteFilter = (field, value) => {
    setQuoteFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearBookingFilters = () => {
    setBookingFilters({ searchText: '', startDate: '', endDate: '' });
  };

  const clearQuoteFilters = () => {
    setQuoteFilters({ searchText: '', startDate: '', endDate: '' });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
      in_transit: 'bg-purple-100 text-purple-800 border-purple-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      completed: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
  };

  const downloadQuotePDF = async (quoteId) => {
    try {
      alert('PDF download feature coming soon!');
    } catch (err) {
      alert('Failed to download PDF');
    }
  };

  // FilterBar Component
  const FilterBar = ({ filters, updateFilter, clearFilters, placeholder = "Search..." }) => {
    return (
      <div className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search Input */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              placeholder={placeholder}
              value={filters.searchText}
              onChange={(e) => updateFilter('searchText', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => updateFilter('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => updateFilter('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Clear Button */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300 transition"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Customer Portal</h1>
              <p className="text-sm text-gray-600">{email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-white bg-red-600 hover:bg-red-700 px-4 py-2 border border-red-600 rounded-lg transition font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'bookings'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              My Bookings ({summary?.total_bookings || 0})
            </button>
            <button
              onClick={() => setActiveTab('quotes')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'quotes'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Quote History ({summary?.total_quotes || 0})
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              My Profile
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Dashboard Summary</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Active Bookings Card - Clickable */}
              <button
                onClick={() => {
                  setDashboardFilter('active');
                  setActiveTab('bookings');
                }}
                className={`bg-white rounded-lg shadow p-6 transition-all cursor-pointer hover:shadow-lg ${
                  dashboardFilter === 'active' ? 'ring-2 ring-blue-600 bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                    <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div className="ml-4 text-left">
                    <p className="text-sm font-medium text-gray-600">Active Bookings</p>
                    <p className="text-2xl font-bold text-gray-900">{summary?.active_bookings || 0}</p>
                  </div>
                </div>
                {dashboardFilter === 'active' && (
                  <div className="mt-2 text-xs text-blue-600 font-medium">Click to view all</div>
                )}
              </button>

              {/* Completed Bookings Card - Clickable */}
              <button
                onClick={() => {
                  setDashboardFilter('completed');
                  setActiveTab('bookings');
                }}
                className={`bg-white rounded-lg shadow p-6 transition-all cursor-pointer hover:shadow-lg ${
                  dashboardFilter === 'completed' ? 'ring-2 ring-green-600 bg-green-50' : ''
                }`}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4 text-left">
                    <p className="text-sm font-medium text-gray-600">Completed</p>
                    <p className="text-2xl font-bold text-gray-900">{summary?.completed_bookings || 0}</p>
                  </div>
                </div>
                {dashboardFilter === 'completed' && (
                  <div className="mt-2 text-xs text-green-600 font-medium">Click to view all</div>
                )}
              </button>

              {/* Total Quotes Card - Clickable */}
              <button
                onClick={() => {
                  setDashboardFilter('quotes');
                  setActiveTab('quotes');
                }}
                className={`bg-white rounded-lg shadow p-6 transition-all cursor-pointer hover:shadow-lg ${
                  dashboardFilter === 'quotes' ? 'ring-2 ring-purple-600 bg-purple-50' : ''
                }`}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                    <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <div className="ml-4 text-left">
                    <p className="text-sm font-medium text-gray-600">Total Quotes</p>
                    <p className="text-2xl font-bold text-gray-900">{summary?.total_quotes || 0}</p>
                  </div>
                </div>
                {dashboardFilter === 'quotes' && (
                  <div className="mt-2 text-xs text-purple-600 font-medium">Click to view all</div>
                )}
              </button>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => window.location.href = '/get-quote'}
                  className="flex items-center justify-center px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Get New Quote
                </button>
                <button
                  onClick={() => setActiveTab('bookings')}
                  className="flex items-center justify-center px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Track My Bookings
                </button>
              </div>
            </div>

            {/* Recent Bookings */}
            {bookings.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Bookings</h3>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="divide-y divide-gray-200">
                    {bookings.slice(0, 5).map((booking) => (
                      <div key={booking.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => viewBookingDetails(booking)}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-blue-600">{booking.booking_reference}</p>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(booking.status)}`}>
                                {getStatusLabel(booking.status)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {booking.pickup_location} → {booking.delivery_location}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(booking.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">${Math.round(booking.total_rate)}</p>
                            <p className="text-xs text-gray-500">{booking.container_type}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">My Bookings ({filterBookings(bookings).length}/{bookings.length})</h2>
              <button
                onClick={() => window.location.href = '/get-quote'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
              >
                Create New Booking
              </button>
            </div>

            {/* Active Filter Indicator */}
            {(dashboardFilter === 'active' || dashboardFilter === 'completed') && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-900">
                    Showing {dashboardFilter === 'active' ? 'Active' : 'Completed'} Bookings Only
                  </span>
                </div>
                <button
                  onClick={() => setDashboardFilter('all')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear Filter
                </button>
              </div>
            )}

            <FilterBar 
              filters={bookingFilters} 
              updateFilter={updateBookingFilter}
              clearFilters={clearBookingFilters}
              placeholder="Search by Booking #, Container, BOL..."
            />

            {filterBookings(bookings).length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500">
                  {bookings.length === 0 ? 'No bookings yet.' : 'No bookings match your filters.'}
                </p>
                {bookings.length === 0 ? (
                  <button
                    onClick={() => window.location.href = '/get-quote'}
                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Create Your First Booking
                  </button>
                ) : (
                  <button
                    onClick={clearBookingFilters}
                    className="mt-4 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {filterBookings(bookings).map((booking) => (
                    <div key={booking.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-blue-600">{booking.booking_reference}</h3>
                            <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(booking.status)}`}>
                              {getStatusLabel(booking.status)}
                            </span>
                          </div>
                          <p className="text-gray-700 mb-1">
                            <strong>Route:</strong> {booking.pickup_location} → {booking.delivery_location}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Container:</strong> {booking.container_type} | <strong>Weight:</strong> {booking.cargo_weight.toLocaleString()} lbs
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            Booked on: {new Date(booking.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-2xl font-bold text-gray-900">${Math.round(booking.total_rate)}</p>
                          <button
                            onClick={() => viewBookingDetails(booking)}
                            className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View Details →
                          </button>
                        </div>
                      </div>
                      
                      {/* Container/BOL Summary */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-gray-700 mb-1">Containers:</p>
                            {booking.container_trailer_numbers?.map((item, idx) => (
                              <p key={idx} className="text-gray-600">
                                {item.number} - <span className="text-xs">{getStatusLabel(item.status)}</span>
                              </p>
                            ))}
                          </div>
                          <div>
                            <p className="font-medium text-gray-700 mb-1">BOL Numbers:</p>
                            {booking.bill_of_lading_numbers?.map((item, idx) => (
                              <p key={idx} className="text-gray-600">
                                {item.number} - <span className="text-xs">{getStatusLabel(item.status)}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Quote History ({filterQuotes(quotes).length}/{quotes.length})</h2>

            <FilterBar 
              filters={quoteFilters} 
              updateFilter={updateQuoteFilter}
              clearFilters={clearQuoteFilters}
              placeholder="Search by Location, Container Type..."
            />

            {filterQuotes(quotes).length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500">
                  {quotes.length === 0 ? 'No quotes yet.' : 'No quotes match your filters.'}
                </p>
                {quotes.length === 0 ? (
                  <button
                    onClick={() => window.location.href = '/get-quote'}
                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Get Your First Quote
                  </button>
                ) : (
                  <button
                    onClick={clearQuoteFilters}
                    className="mt-4 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filterQuotes(quotes).map((quote) => (
                  <div key={quote.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Quote Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(quote.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Rate</p>
                        <p className="text-xl font-bold text-green-600">${Math.round(quote.total_rate)}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <p className="text-sm text-gray-600">
                        <strong>From:</strong> {quote.pickup_location}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>To:</strong> {quote.delivery_location}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Container:</strong> {quote.container_type}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Distance:</strong> {quote.distance} miles
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewQuote(quote)}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => downloadQuotePDF(quote.id)}
                        className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 transition"
                      >
                        PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <PortalProfile token={localStorage.getItem('portal_token')} />
        )}
      </main>

      {/* Booking Details Modal */}
      {showBookingModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedBooking.booking_reference}</h3>
                  <span className={`inline-block mt-2 px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(selectedBooking.status)}`}>
                    {getStatusLabel(selectedBooking.status)}
                  </span>
                  {selectedBooking.status === 'pending' && !isEditingBooking && (
                    <p className="text-sm text-blue-600 mt-2">✏️ You can edit this booking</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowBookingModal(false);
                    setIsEditingBooking(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Shipment Details */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">📦 Shipment Details</h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Location
                      </label>
                      {isEditingBooking ? (
                        <input
                          type="text"
                          value={editedBooking.pickup_location}
                          onChange={(e) => setEditedBooking({...editedBooking, pickup_location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedBooking.pickup_location}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Location
                      </label>
                      {isEditingBooking ? (
                        <input
                          type="text"
                          value={editedBooking.delivery_location}
                          onChange={(e) => setEditedBooking({...editedBooking, delivery_location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedBooking.delivery_location}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pickup Date
                        </label>
                        {isEditingBooking ? (
                          <input
                            type="date"
                            value={editedBooking.pickup_date || ''}
                            onChange={(e) => setEditedBooking({...editedBooking, pickup_date: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-gray-900">{selectedBooking.pickup_date || 'Not specified'}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Delivery Date
                        </label>
                        {isEditingBooking ? (
                          <input
                            type="date"
                            value={editedBooking.delivery_date || ''}
                            onChange={(e) => setEditedBooking({...editedBooking, delivery_date: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-gray-900">{selectedBooking.delivery_date || 'Not specified'}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pickup Time
                        </label>
                        {isEditingBooking ? (
                          <input
                            type="time"
                            value={editedBooking.pickup_time || ''}
                            onChange={(e) => setEditedBooking({...editedBooking, pickup_time: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="HH:MM"
                          />
                        ) : (
                          <p className="text-gray-900">{selectedBooking.pickup_time || 'Not specified'}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Delivery Time
                        </label>
                        {isEditingBooking ? (
                          <input
                            type="time"
                            value={editedBooking.delivery_time || ''}
                            onChange={(e) => setEditedBooking({...editedBooking, delivery_time: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="HH:MM"
                          />
                        ) : (
                          <p className="text-gray-900">{selectedBooking.delivery_time || 'Not specified'}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Distance
                        </label>
                        <p className="text-gray-900">{selectedBooking.distance} miles</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Container Type
                        </label>
                        {isEditingBooking ? (
                          <select
                            value={editedBooking.container_type}
                            onChange={(e) => setEditedBooking({...editedBooking, container_type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="20ft Standard">20ft Standard</option>
                            <option value="40ft Standard">40ft Standard</option>
                            <option value="40ft High Cube">40ft High Cube</option>
                            <option value="45ft High Cube">45ft High Cube</option>
                            <option value="53ft Trailer">53ft Trailer</option>
                          </select>
                        ) : (
                          <p className="text-gray-900">{selectedBooking.container_type}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Weight
                        </label>
                        {isEditingBooking ? (
                          <input
                            type="number"
                            value={editedBooking.cargo_weight}
                            onChange={(e) => setEditedBooking({...editedBooking, cargo_weight: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-gray-900">{selectedBooking.cargo_weight?.toLocaleString()} lbs</p>
                        )}
                      </div>
                    </div>

                    {/* Chassis Usage Days */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        🚛 Chassis Usage (Days)
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Regular Chassis Days
                          </label>
                          {isEditingBooking ? (
                            <input
                              type="number"
                              min="0"
                              value={editedBooking.regular_chassis_days || 0}
                              onChange={(e) => setEditedBooking({...editedBooking, regular_chassis_days: parseInt(e.target.value) || 0})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="0 days"
                            />
                          ) : (
                            <p className="text-gray-900">
                              {selectedBooking.regular_chassis_days || 0} day(s)
                              {selectedBooking.regular_chassis_days > 0 && (
                                <span className="text-xs text-gray-500 ml-2">
                                  (${(selectedBooking.regular_chassis_days * 40).toFixed(2)})
                                </span>
                              )}
                            </p>
                          )}
                          {isEditingBooking && (
                            <p className="text-xs text-gray-500 mt-1">
                              $40/day • Minimum 1 day if used
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Triaxle Chassis Days
                          </label>
                          {isEditingBooking ? (
                            <input
                              type="number"
                              min="0"
                              value={editedBooking.triaxle_chassis_days || 0}
                              onChange={(e) => setEditedBooking({...editedBooking, triaxle_chassis_days: parseInt(e.target.value) || 0})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="0 days"
                            />
                          ) : (
                            <p className="text-gray-900">
                              {selectedBooking.triaxle_chassis_days || 0} day(s)
                              {selectedBooking.triaxle_chassis_days > 0 && (
                                <span className="text-xs text-gray-500 ml-2">
                                  (${(selectedBooking.triaxle_chassis_days * 75).toFixed(2)})
                                </span>
                              )}
                            </p>
                          )}
                          {isEditingBooking && (
                            <p className="text-xs text-gray-500 mt-1">
                              $75/day • Minimum 1 day if used
                            </p>
                          )}
                        </div>
                      </div>
                      {isEditingBooking && (
                        <p className="text-xs text-blue-600 mt-2">
                          💡 Set to 0 if not using chassis. If using, minimum 1 day required.
                        </p>
                      )}
                    </div>

                    {(selectedBooking.special_instructions || isEditingBooking) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Special Instructions
                        </label>
                        {isEditingBooking ? (
                          <textarea
                            value={editedBooking.special_instructions || ''}
                            onChange={(e) => setEditedBooking({...editedBooking, special_instructions: e.target.value})}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Any special requirements..."
                          />
                        ) : (
                          <p className="text-gray-900">{selectedBooking.special_instructions || 'None'}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Container/Trailer Numbers */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-800">🚛 Container/Trailer Numbers</h4>
                    {isEditingBooking && (
                      <button
                        onClick={handleAddContainerNumber}
                        className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        + Add
                      </button>
                    )}
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {isEditingBooking ? (
                      editedBooking.container_trailer_numbers?.length > 0 ? (
                        editedBooking.container_trailer_numbers.map((item, idx) => (
                          <div key={idx} className="flex gap-2">
                            <input
                              type="text"
                              value={item.number}
                              onChange={(e) => handleUpdateContainerNumber(idx, e.target.value)}
                              placeholder="Enter container/trailer number"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              onClick={() => handleRemoveContainerNumber(idx)}
                              className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No container numbers yet. Click "+ Add" to add one.</p>
                      )
                    ) : (
                      selectedBooking.container_trailer_numbers?.length > 0 ? (
                        selectedBooking.container_trailer_numbers.map((item, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span>{item.number}</span>
                            <span className={`px-2 py-1 text-xs rounded ${getStatusColor(item.status)}`}>
                              {getStatusLabel(item.status)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No container numbers assigned yet</p>
                      )
                    )}
                  </div>
                </div>

                {/* BOL Numbers */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-800">📋 BOL/Booking Numbers</h4>
                    {isEditingBooking && (
                      <button
                        onClick={handleAddBolNumber}
                        className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        + Add
                      </button>
                    )}
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    {isEditingBooking ? (
                      editedBooking.bol_numbers?.length > 0 ? (
                        editedBooking.bol_numbers.map((bol, idx) => (
                          <div key={idx} className="flex gap-2">
                            <input
                              type="text"
                              value={bol}
                              onChange={(e) => handleUpdateBolNumber(idx, e.target.value)}
                              placeholder="Enter BOL/Booking number"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              onClick={() => handleRemoveBolNumber(idx)}
                              className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No BOL numbers yet. Click "+ Add" to add one.</p>
                      )
                    ) : (
                      selectedBooking.bol_numbers?.length > 0 ? (
                        selectedBooking.bol_numbers.map((bol, idx) => (
                          <div key={idx}>
                            <span>{bol}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No BOL numbers assigned yet</p>
                      )
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Rate Breakdown</h4>
                  <div className="bg-gray-50 p-4 rounded space-y-2">
                    <div className="flex justify-between">
                      <span>Base Rate:</span>
                      <span>${selectedBooking.base_rate.toFixed(2)}</span>
                    </div>
                    {selectedBooking.accessorials?.map((acc, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{acc.name}:</span>
                        <span>${acc.amount.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t pt-2 flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span className="text-green-600">${Math.round(selectedBooking.total_rate)}</span>
                    </div>
                  </div>
                </div>

                {/* Rate Breakdown - Always visible, never editable */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">💰 Rate Breakdown</h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span>Base Rate:</span>
                      <span>${selectedBooking.base_rate?.toFixed(2) || '0.00'}</span>
                    </div>
                    {selectedBooking.accessorials?.map((acc, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{acc.name}:</span>
                        <span>${acc.amount?.toFixed(2) || '0.00'}</span>
                      </div>
                    ))}
                    <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span className="text-green-600">${Math.round(selectedBooking.total_rate)}</span>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="pt-4 text-sm text-gray-600 space-y-1">
                  <p><strong>Booked on:</strong> {new Date(selectedBooking.created_at).toLocaleString()}</p>
                  {selectedBooking.updated_at && (
                    <p><strong>Last updated:</strong> {new Date(selectedBooking.updated_at).toLocaleString()}</p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3 justify-end">
                {selectedBooking.status === 'pending' ? (
                  isEditingBooking ? (
                    <>
                      <button
                        onClick={handleCancelBookingEdit}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveBooking}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                      >
                        💾 Save Changes
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setShowBookingModal(false);
                          setIsEditingBooking(false);
                        }}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                      >
                        Close
                      </button>
                      <button
                        onClick={handleEditBooking}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        ✏️ Edit Booking
                      </button>
                    </>
                  )
                ) : (
                  <button
                    onClick={() => {
                      setShowBookingModal(false);
                      setIsEditingBooking(false);
                    }}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Details Modal */}
      {showQuoteModal && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Quote Details</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Created: {new Date(selectedQuote.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => setShowQuoteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Shipment Details */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    📦 Shipment Information
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Location
                      </label>
                      {isEditingQuote ? (
                        <input
                          type="text"
                          value={editedQuote.pickup_location}
                          onChange={(e) => setEditedQuote({...editedQuote, pickup_location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedQuote.pickup_location}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Delivery Location
                      </label>
                      {isEditingQuote ? (
                        <input
                          type="text"
                          value={editedQuote.delivery_location}
                          onChange={(e) => setEditedQuote({...editedQuote, delivery_location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{selectedQuote.delivery_location}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Container Type
                        </label>
                        {isEditingQuote ? (
                          <select
                            value={editedQuote.container_type}
                            onChange={(e) => setEditedQuote({...editedQuote, container_type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="20ft Standard">20ft Standard</option>
                            <option value="40ft Standard">40ft Standard</option>
                            <option value="40ft High Cube">40ft High Cube</option>
                            <option value="45ft High Cube">45ft High Cube</option>
                            <option value="53ft Trailer">53ft Trailer</option>
                          </select>
                        ) : (
                          <p className="text-gray-900">{selectedQuote.container_type}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cargo Weight
                        </label>
                        {isEditingQuote ? (
                          <input
                            type="number"
                            value={editedQuote.cargo_weight}
                            onChange={(e) => setEditedQuote({...editedQuote, cargo_weight: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-gray-900">{selectedQuote.cargo_weight?.toLocaleString() || 'N/A'} lbs</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pickup Date
                        </label>
                        {isEditingQuote ? (
                          <input
                            type="date"
                            value={editedQuote.pickup_date || ''}
                            onChange={(e) => setEditedQuote({...editedQuote, pickup_date: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-gray-900">{selectedQuote.pickup_date || 'Not specified'}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Delivery Date
                        </label>
                        {isEditingQuote ? (
                          <input
                            type="date"
                            value={editedQuote.delivery_date || ''}
                            onChange={(e) => setEditedQuote({...editedQuote, delivery_date: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <p className="text-gray-900">{selectedQuote.delivery_date || 'Not specified'}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Distance
                      </label>
                      <p className="text-gray-900">{selectedQuote.distance} miles</p>
                    </div>

                    {(selectedQuote.special_instructions || isEditingQuote) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Special Instructions
                        </label>
                        {isEditingQuote ? (
                          <textarea
                            value={editedQuote.special_instructions || ''}
                            onChange={(e) => setEditedQuote({...editedQuote, special_instructions: e.target.value})}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Any special requirements or notes..."
                          />
                        ) : (
                          <p className="text-gray-900">{selectedQuote.special_instructions || 'None'}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Rate Information */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    💰 Rate Breakdown
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Base Rate:</span>
                      <span className="font-medium">${selectedQuote.base_rate?.toFixed(2) || '0.00'}</span>
                    </div>
                    {selectedQuote.accessorials?.map((acc, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span className="text-gray-700">{acc.name}:</span>
                        <span className="font-medium">${acc.amount?.toFixed(2) || '0.00'}</span>
                      </div>
                    ))}
                    <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between">
                      <span className="font-semibold text-lg">Total Rate:</span>
                      <span className="font-bold text-xl text-green-600">
                        ${Math.round(selectedQuote.total_rate)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                {(selectedQuote.customer_name || selectedQuote.customer_email) && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      👤 Contact Information
                    </h4>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      {selectedQuote.customer_name && (
                        <p><strong>Name:</strong> {selectedQuote.customer_name}</p>
                      )}
                      {selectedQuote.customer_email && (
                        <p><strong>Email:</strong> {selectedQuote.customer_email}</p>
                      )}
                      {selectedQuote.company_name && (
                        <p><strong>Company:</strong> {selectedQuote.company_name}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3 justify-end flex-wrap">
                {isEditingQuote ? (
                  <>
                    <button
                      onClick={handleCancelEdit}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveQuote}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      Save Changes
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setShowQuoteModal(false)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                    >
                      Close
                    </button>
                    <button
                      onClick={handleEditQuote}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      ✏️ Edit Quote
                    </button>
                    <button
                      onClick={handleConvertToBooking}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      📦 Convert to Booking
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PortalDashboard;
